import { useState, useMemo } from "react";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  Popover,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  ArrowDropDown,
} from "@mui/icons-material";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

type CalendarValue = Date | null | [Date | null, Date | null];
type ViewMode = "daily" | "weekly";
type NavigatorMode = "daily" | "weekly" | "both";

interface DateNavigatorProps {
  /** The mode of the navigator: daily-only, weekly-only, or both with toggle */
  mode: NavigatorMode;
  /** Current selected date (YYYY-MM-DD format) */
  selectedDate: string;
  /** Callback when date changes */
  onDateChange: (date: string) => void;
  /** Current view mode when mode="both" */
  viewMode?: ViewMode;
  /** Callback when view mode changes (only used when mode="both") */
  onViewModeChange?: (mode: ViewMode) => void;
  /** Optional right-side content (e.g., additional buttons) */
  rightContent?: React.ReactNode;
  /** Whether to show margin bottom (default: true) */
  marginBottom?: boolean;
}

// Helper to format date as YYYY-MM-DD
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to get Monday of the week containing the date
const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : -(day - 1);
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper to get ISO week number
const getISOWeek = (date: Date): number => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
};

export default function DateNavigator({
  mode,
  selectedDate,
  onDateChange,
  viewMode = "weekly",
  onViewModeChange,
  rightContent,
  marginBottom = true,
}: DateNavigatorProps) {
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);

  // Determine effective view mode based on navigator mode
  const effectiveViewMode = mode === "daily" ? "daily" : mode === "weekly" ? "weekly" : viewMode;

  // Calculate week start for weekly mode
  const weekStart = useMemo(() => {
    return getWeekStart(new Date(selectedDate + "T00:00:00"));
  }, [selectedDate]);

  // Calculate week end
  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(weekStart.getDate() + 6);
    return end;
  }, [weekStart]);

  // Format display date
  const displayDate = useMemo(() => {
    if (effectiveViewMode === "daily") {
      const dateObj = new Date(selectedDate + "T00:00:00");
      const day = dateObj.getDate();
      const month = dateObj.toLocaleDateString("en-US", { month: "short" });
      const year = dateObj.getFullYear();
      const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
      return `${weekday}, ${day} ${month} ${year}`;
    } else {
      const startDay = weekStart.getDate();
      const endDay = weekEnd.getDate();
      const startMonth = weekStart.toLocaleDateString("en-US", { month: "short" });
      const endMonth = weekEnd.toLocaleDateString("en-US", { month: "short" });
      const year = weekStart.getFullYear();
      return startMonth === endMonth
        ? `${startDay}–${endDay} ${startMonth} ${year}`
        : `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
    }
  }, [effectiveViewMode, selectedDate, weekStart, weekEnd]);

  // Get today's formatted date for tooltip
  const formattedToday = useMemo(() => {
    const today = new Date();
    return today.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  // Navigation functions
  const navigatePrevious = () => {
    const date = new Date(selectedDate + "T00:00:00");
    date.setDate(date.getDate() - (effectiveViewMode === "daily" ? 1 : 7));
    onDateChange(formatDate(date));
  };

  const navigateNext = () => {
    const date = new Date(selectedDate + "T00:00:00");
    date.setDate(date.getDate() + (effectiveViewMode === "daily" ? 1 : 7));
    onDateChange(formatDate(date));
  };

  const goToToday = () => {
    const today = new Date();
    if (effectiveViewMode === "weekly") {
      onDateChange(formatDate(getWeekStart(today)));
    } else {
      onDateChange(formatDate(today));
    }
  };

  const handleDateClick = (event: React.MouseEvent<HTMLElement>) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (value: CalendarValue) => {
    if (value && value instanceof Date) {
      if (effectiveViewMode === "weekly") {
        onDateChange(formatDate(getWeekStart(value)));
      } else {
        onDateChange(formatDate(value));
      }
      handleDatePickerClose();
    }
  };

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode && onViewModeChange) {
      onViewModeChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: marginBottom ? 3 : 0,
      }}
    >
      {/* Today Button - Leftmost */}
      <Tooltip title={formattedToday} arrow placement="top">
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "action.hover",
            },
          }}
        >
          <IconButton onClick={goToToday} size="medium" color="primary">
            <CalendarMonth />
          </IconButton>
        </Paper>
      </Tooltip>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {/* Previous Button */}
        <Tooltip
          title={effectiveViewMode === "daily" ? "Previous Day" : "Previous Week"}
          arrow
          placement="top"
        >
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            <IconButton onClick={navigatePrevious} size="medium">
              <ChevronLeft />
            </IconButton>
          </Paper>
        </Tooltip>

        {/* Date Range - Center (Clickable) */}
        <Tooltip
          title={`Click to select a ${effectiveViewMode === "daily" ? "date" : "week"}`}
          arrow
          placement="top"
        >
          <Paper
            elevation={0}
            onClick={handleDateClick}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
                transform: "translateY(-2px)",
                boxShadow: 2,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                columnGap: 1.5,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                cursor: "pointer",
              }}
            >
              {/* Date range */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ letterSpacing: 0.4 }}
                >
                  {displayDate}
                </Typography>
              </Box>

              {/* Divider and Week number (only in weekly mode) */}
              {effectiveViewMode === "weekly" && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Week {getISOWeek(weekStart)}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Dropdown indicator */}
              <IconButton
                size="small"
                sx={{
                  ml: 0.5,
                  color: "text.secondary",
                  pointerEvents: "none",
                }}
              >
                <ArrowDropDown />
              </IconButton>
            </Box>
          </Paper>
        </Tooltip>

        {/* Next Button */}
        <Tooltip
          title={effectiveViewMode === "daily" ? "Next Day" : "Next Week"}
          arrow
          placement="top"
        >
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            <IconButton onClick={navigateNext} size="medium">
              <ChevronRight />
            </IconButton>
          </Paper>
        </Tooltip>

        {/* Date Picker Popover */}
        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleDatePickerClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          sx={{ mt: 1 }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5 }}>
              {effectiveViewMode === "daily"
                ? "Select a date"
                : "Select a date to jump to that week"}
            </Typography>
            <Calendar
              onChange={handleDateChange}
              value={new Date(selectedDate + "T00:00:00")}
              calendarType="iso8601"
              locale="en-US"
            />
          </Box>
        </Popover>
      </Box>

      {/* View Mode Toggle (only when mode="both") */}
      {mode === "both" && onViewModeChange && (
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            "& .MuiToggleButton-root": {
              px: 3,
              py: 0.75,
              textTransform: "none",
              fontWeight: 500,
              border: "none",
            },
            "& .Mui-selected": {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            },
          }}
        >
          <ToggleButton value="daily">Day</ToggleButton>
          <ToggleButton value="weekly">Week</ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* Optional right content */}
      {rightContent}
    </Box>
  );
}
